import React from "react";

import cx from "classnames";

import { BRANDING_CLASSES } from "../../styles/branding";

import colors from "./Illustration.scss";
import { IllustrationMap } from "./IllustrationMap";
import { IllustrationDictionary } from "./types";

export interface IllustrationProps {
  /**
   * Which illustration to use
   */
  illustration: IllustrationDictionary;
  /**
   * Which color to use for the illustration. Uses brand color if not supplied.
   */
  color?: string;
  /**
   * Size of the illustration, in square pixels. Defaults to 120
   */
  size?: 48 | 64 | 84 | 96 | 120 | 160 | 196;
  /**
   * Optional data-testid attribute for testing
   */
  "data-testid"?: string;
}

/**
 * A component housing some prefab illustrations to use across the application.
 */
export function Illustration({
  illustration,
  color = colors.brandPrimary,
  size = 120,
  "data-testid": dataTestId,
}: IllustrationProps) {
  const { illustrationClass, illustrationColor, sizeStyle } =
    React.useMemo(() => {
      const sizeStyle = {
        width: size,
        height: size,
      };

      let illustrationColor = "currentColor";
      let illustrationClass = "";

      if (color === colors.brandPrimary || color === colors.brandSecondary) {
        if (color === colors.brandPrimary) {
          illustrationClass = cx(BRANDING_CLASSES.PRIMARY_COLOR_FOREGROUND);
        } else {
          illustrationClass = cx(BRANDING_CLASSES.SECONDARY_COLOR_FOREGROUND);
        }
      } else {
        illustrationColor = color;
      }

      return {
        sizeStyle,
        illustrationColor,
        illustrationClass,
      };
    }, [size, color]);

  const IllustrationComponent = IllustrationMap[illustration];

  return (
    <svg
      viewBox="0 0 120 120"
      role="img"
      style={sizeStyle}
      className={illustrationClass}
      data-testid={`${dataTestId || "illustration"}-svg`}
    >
      <IllustrationComponent illustrationColor={illustrationColor} />
    </svg>
  );
}
